import { useMemo } from 'react';
// Mirrors utils/rolePermissions.js on the backend field-for-field and
// flag-for-flag. This hook controls what's *shown*; the backend controls
// what's actually *allowed*. Keep the two in sync — see
// ROLES-PERMISSIONS-TIDYUP-SCOPE.md for why that matters.
const BROKER_LEVEL_RANK     = { owner: 3, admin: 3, manager: 2, adviser: 1 };
const INTRODUCER_LEVEL_RANK = { company: 3, solo: 3, manager: 2, adviser: 1 };
export function usePermissions() {
  return useMemo(() => {
    const raw = localStorage.getItem('user');
    if (!raw) return guest();
    let user;
    try { user = JSON.parse(raw); } catch { return guest(); }
    const role        = user.role || '';
    const brokerLevel = user.broker_level || user.brokerLevel || null;
    const intLevel    = user.introducerLevel || user.introducer_level || null;
    const introducerId = user.introducerId || user.introducer_id || null;
    const managerId   = user.manager_id || user.managerId || null;
    const isPlatformOwner = role === 'platform_owner' || role === 'admin';
    const isIntroducer    = role === 'introducer';
    const brokerRank      = BROKER_LEVEL_RANK[brokerLevel] || 0;
    const isBroker         = !isIntroducer && !isPlatformOwner;
    // isBrokerOwner = "has owner-level access" (rank-based: owner OR admin).
    // Drives every canX flag below that means "full company access."
    const isBrokerOwner   = brokerRank >= 3;
    const isManager       = brokerRank >= 2;
    const isAdviser       = brokerRank === 1;
    // isCompanyDirector = exact match on 'owner' only — the narrow flag for
    // legal/financial authority (billing, agreement signing). NOT extended
    // to admins, per Tim's explicit decision.
    const isCompanyDirector = isBroker && brokerLevel === 'owner';
    const isCompanyAdmin    = isBroker && brokerLevel === 'admin';
    const intRank         = INTRODUCER_LEVEL_RANK[intLevel] || 0;
    const isIntCompany    = intRank >= 3;
    const isIntManager    = intRank >= 2;
    const isIntAdviser    = intLevel === 'adviser';
    let dealScopeLabel, introducerScopeLabel;
    if (isIntroducer) {
      dealScopeLabel       = isIntCompany ? 'All org deals' : isIntManager ? "Your team's deals" : 'Your deals';
      introducerScopeLabel = 'Your organisation';
    } else if (isPlatformOwner || isBrokerOwner) {
      dealScopeLabel = introducerScopeLabel = 'Company-wide';
    } else if (isManager) {
      dealScopeLabel = "Your team's deals"; introducerScopeLabel = 'Company-wide';
    } else {
      dealScopeLabel = 'Your deals'; introducerScopeLabel = 'Your introducers';
    }
    return {
      user, role, brokerLevel, introducerLevel: intLevel, introducerId, managerId,
      isPlatformOwner, isIntroducer, isBroker, isBrokerOwner, isCompanyDirector, isCompanyAdmin,
      isManager, isAdviser,
      isIntCompany, isIntManager, isIntAdviser,
      canViewAllDeals: isPlatformOwner || isBrokerOwner,
      canViewTeamDeals: isManager || isIntCompany || isIntManager,
      canCreateDeal: !isIntroducer,
      canEditDeal: isPlatformOwner || isBrokerOwner || isManager,
      canDeleteDeal: isPlatformOwner || isBrokerOwner,
      canOverrideFee: isPlatformOwner || isBrokerOwner || isManager,
      canMarkPaid: isPlatformOwner || isBrokerOwner,
      canViewAllIntroducers: isPlatformOwner || isBrokerOwner || isManager,
      canCreateIntroducer: isPlatformOwner || isBrokerOwner || isManager,
      canEditIntroducer: isPlatformOwner || isBrokerOwner,
      canDeleteIntroducer: isPlatformOwner || isBrokerOwner,
      canViewTeam: isPlatformOwner || isManager || isIntCompany || isIntManager,
      canAddAdviser: isPlatformOwner || isManager,
      canAddManager: isPlatformOwner || isBrokerOwner,
      canManageUsers: isPlatformOwner,
      canEditUser: isPlatformOwner || isBrokerOwner,
      canDeleteUser: isPlatformOwner,
      canManageTeamUsers: isPlatformOwner || isManager,
      canViewCommissions: true,
      canViewAllCommissions: isPlatformOwner || isBrokerOwner,
      canReviewLeads: !isIntroducer,
      canEditCompanySettings: isPlatformOwner || isBrokerOwner,
      canViewAuditLog: isPlatformOwner,
      // Director-only (NOT extended to admin) — billing/legal authority.
      canManageSubscription: isPlatformOwner || isCompanyDirector,
      canAcceptAgreement: isPlatformOwner || isCompanyDirector,
      dealScopeLabel, introducerScopeLabel,
    };
  }, []);
}
function guest() {
  return {
    user: null, role: '', brokerLevel: null, introducerLevel: null,
    introducerId: null, managerId: null,
    isPlatformOwner: false, isIntroducer: false, isBroker: false,
    isBrokerOwner: false, isCompanyDirector: false, isCompanyAdmin: false,
    isManager: false, isAdviser: false,
    isIntCompany: false, isIntManager: false, isIntAdviser: false,
    canViewAllDeals: false, canViewTeamDeals: false, canCreateDeal: false,
    canEditDeal: false, canDeleteDeal: false, canOverrideFee: false, canMarkPaid: false,
    canViewAllIntroducers: false, canCreateIntroducer: false,
    canEditIntroducer: false, canDeleteIntroducer: false,
    canViewTeam: false, canAddAdviser: false, canAddManager: false,
    canManageUsers: false, canEditUser: false, canDeleteUser: false, canManageTeamUsers: false,
    canViewCommissions: false, canViewAllCommissions: false, canReviewLeads: false,
    canEditCompanySettings: false, canViewAuditLog: false,
    canManageSubscription: false, canAcceptAgreement: false,
    dealScopeLabel: '', introducerScopeLabel: '',
  };
}
export default usePermissions;
